import _ from 'lodash'
import React from 'react'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Checkbox,
  IconButton
} from '@material-ui/core'
import Loading from './Loading'
import ExpandMoreSharpIcon from '@material-ui/icons/ExpandMoreSharp'

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    padding: theme.spacing(0, 2),
    paddingBottom: theme.spacing(1),
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  subTable: {
    opacity: 0.9,
    background: 'rgba(0,0,0,.05)',
  },
}))

export default function WTable(props) {
  const {
    loading = false,
    columns,
    dataSource,
    indexKey,
    tableTitle,
    pagination,
    multiSelect,
    singleSelect,
    onChange,
    onSelectionChange,
    childRow,
    renderToolbar: PropsToolbar,
  } = props
  const classes = useStyles()

  const [sortInfo, setSortInfo] = React.useState({
    orderBy: null,
    order: 'asc',
  })
  const [openedSubTable, setOpenedSubTable] = React.useState([]);
  const handleSubTable = (id) => {
    const newOpened = [...openedSubTable]
    const index = openedSubTable.indexOf(id);
    if (index > -1) {
      newOpened.splice(index, 1)
    } else {
      newOpened.push(id);
    }
    setOpenedSubTable(newOpened)
  }
  const [selected, setSelected] = React.useState([])

  function handleRequestSort(event, property) {
    const isDesc = sortInfo.orderBy === property && sortInfo.order === 'desc'

    const newSortInfo = {
      ...sortInfo,
      orderBy: property,
      order: isDesc ? 'asc' : 'desc',
    }
    setSortInfo(newSortInfo)
    handleChange('sort', newSortInfo)
  }

  function handleSelectAllClick(event) {
    if (event.target.checked) {
      const newSelecteds = dataSource.map(n => n[indexKey]);

      setSelected(newSelecteds);
      if (_.isFunction(onSelectionChange)) onSelectionChange(newSelecteds);

      return;
    }

    setSelected([]);
    if (_.isFunction(onSelectionChange)) onSelectionChange([]);
  }

  function handleClick(value) {
    const selectedIndex = selected.indexOf(value);
    let newSelected = [];

    if (singleSelect) {
      newSelected.push(value);
    } else {
      newSelected = [
        ...(selectedIndex > -1 ? selected.filter(s => s !== value) : selected),
        ...(selectedIndex === -1 ? [value] : []),
      ];
    }

    setSelected(newSelected);
    if (_.isFunction(onSelectionChange)) onSelectionChange(newSelected);
  }

  function handleChange(action, payload) {
    const newPagination = { ...pagination }
    const newSortInfo = { ...sortInfo }
    if (action === 'paginate') {
      newPagination.page = _.get(payload, 'page', pagination.page)
      newPagination.per_page = _.get(payload, 'per_page', pagination.per_page)
      if (pagination.per_page !== newPagination.per_page) {
        newPagination.page = 0
      }
    }

    if (action === 'sort') {
      newSortInfo.orderBy = _.get(payload, 'orderBy', sortInfo.orderBy)
      newSortInfo.order = _.get(payload, 'order', sortInfo.order)
    }

    if (_.isFunction(onChange)) {
      onChange(newPagination, newSortInfo, {
        action,
        currentDataSource: dataSource,
      })
    }
  }

  const isSelected = name => selected.indexOf(name) !== -1;

  return (
    <>
      {(_.isFunction(PropsToolbar) || !_.isEmpty(tableTitle)) && (
        <Toolbar className={clsx(classes.root, { [classes.highlight]: selected.length > 0 })}>
          {_.isFunction(PropsToolbar) && (
            <PropsToolbar selected numSelected={selected.length} tableTitle={tableTitle} />
          )}
          {!_.isFunction(PropsToolbar) && !_.isEmpty(tableTitle) && (
            <Typography variant="h6" id="tableTitle">
              {tableTitle}
            </Typography>
          )}
        </Toolbar>
      )}

      <Loading loading={loading} wrapper size={30}>
        <div className={classes.tableWrapper}>
          <Table className={classes.table} aria-labelledby="tableTitle" size="small">
            <TableHead>
              <TableRow>
                {!(multiSelect || singleSelect) ? null : (
                  <TableCell padding="checkbox" component="th">
                    <Checkbox
                      disabled={singleSelect}
                      indeterminate={selected.length > 0 && selected.length < dataSource.length}
                      checked={selected.length === dataSource.length}
                      onChange={handleSelectAllClick}
                      inputProps={{ 'aria-label': 'Select all desserts' }}
                    />
                  </TableCell>
                )}
                {columns.map(row => (
                  <TableCell
                    component="th"
                    key={row.id}
                    align={_.get(row, 'align', 'left')}
                    padding={row.disablePadding && (multiSelect || singleSelect) ? 'none' : 'default'}
                    sortDirection={sortInfo.orderBy === row.id ? sortInfo.order : false}
                    style={row.style ? row.style : { width: 120 }}
                  >
                    {_.get(row, 'enableOrder') === true ? (
                      <TableSortLabel
                        active={sortInfo.orderBy === row.id}
                        direction={sortInfo.order}
                        onClick={e => handleRequestSort(e, row.id)}
                      >
                        {row.label}
                      </TableSortLabel>
                    ) : (
                      row.label
                    )}
                  </TableCell>
                ))}
                {childRow && childRow.key && <TableCell>{childRow.label}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {_.map(dataSource, (row, index) => {
                const isItemSelected = isSelected(row[indexKey])
                const labelId = `enhanced-table-checkbox-${index}`

                return (
                  <React.Fragment key={labelId}>
                    <TableRow
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row[indexKey]}
                      selected={isItemSelected}
                    >
                      {!(multiSelect || singleSelect) ? null : (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            inputProps={{ 'aria-labelledby': labelId }}
                            onChange={() => {
                              if (multiSelect || singleSelect)
                                handleClick(row[indexKey]);
                            }}
                          />
                        </TableCell>
                      )}
                      {columns.map((head, idx) => {
                        let tableContent = row[head.id]
                        if (head.render) {
                          tableContent = head.render(row[head.id], row, index)
                        }
                        return (
                          <TableCell
                            id={labelId}
                            scope="row"
                            padding={idx === 0 && (multiSelect || singleSelect) ? 'none' : 'default'}
                            key={head.id}
                            align={_.get(head, 'align', 'left')}
                          >
                            {tableContent}
                          </TableCell>
                        )
                      })}
                      {childRow && childRow.key && (
                        <TableCell style={{ width: 60 }}>
                          {_.get(row[childRow.key], 'length') > 0 && (
                            <IconButton size="small" onClick={() => handleSubTable(row[indexKey])}>
                              <ExpandMoreSharpIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      )}
                    </TableRow>

                    {childRow && childRow.key && _.get(row[childRow.key], 'length') > 0 && (openedSubTable.indexOf(row[indexKey]) > -1) && (
                      <TableRow>
                        <TableCell
                          colSpan={(multiSelect || singleSelect) ? columns.length + 2 : columns.length + 1}
                          style={{ padding: 0 }}
                        >
                          <Table
                            className={`${classes.table} ${classes.subTable}`}
                            aria-labelledby="tableTitle"
                            size="small"
                          >
                            {childRow.header && (
                              <TableHead>
                                <TableRow>
                                  {!(multiSelect || singleSelect) ? null : (
                                    <TableCell component="th" />
                                  )}
                                  {childRow.columns.map(row => (
                                    <TableCell
                                      component="th"
                                      key={row.id}
                                      align={_.get(row, 'align', 'left')}
                                      padding={
                                        row.disablePadding && (multiSelect || singleSelect)
                                          ? 'none' : 'default'
                                      }
                                      sortDirection={
                                        sortInfo.orderBy === row.id ? sortInfo.order : false
                                      }
                                      style={row.style ? row.style : { width: 120 }}
                                      colSpan={_.get(row, 'colSpan', 1)}
                                    >
                                      {_.get(row, 'enableOrder') === true ? (
                                        <TableSortLabel
                                          active={sortInfo.orderBy === row.id}
                                          direction={sortInfo.order}
                                          onClick={e => handleRequestSort(e, row.id)}
                                        >
                                          {row.label}
                                        </TableSortLabel>
                                      ) : (
                                        row.label
                                      )}
                                    </TableCell>
                                  ))}
                                  {childRow && childRow.key && <TableCell component="th" />}
                                </TableRow>
                              </TableHead>
                            )}
                            <TableBody>
                              {_.map(row[childRow.key], (row, index) => {
                                return (
                                  <TableRow key={index}>
                                    {!(multiSelect || singleSelect) ? null : (
                                      <TableCell padding="checkbox" />
                                    )}
                                    {childRow.columns.map((head, idx) => {
                                      let tableContent = row[head.id]
                                      if (head.render) {
                                        tableContent = head.render(row[head.id], row, index)
                                      }
                                      return (
                                        <TableCell
                                          id={labelId}
                                          scope="row"
                                          padding={
                                            idx === 0 && (multiSelect || singleSelect)
                                              ? 'none' : 'default'
                                          }
                                          key={head.id}
                                          align={_.get(head, 'align', 'left')}
                                          colSpan={_.get(head, 'colSpan', 1)}
                                        >
                                          {tableContent}
                                        </TableCell>
                                      )
                                    })}
                                    {childRow && childRow.key && <TableCell component="th" style={{ width: 60 }} align={_.get(row, 'align', 'left')} />}
                                  </TableRow>
                                )
                              })}
                            </TableBody>
                          </Table>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </div>
        {!_.isEmpty(pagination) && (
          <TablePagination
            rowsPerPageOptions={[1, 20, 50, 100]}
            component="div"
            count={pagination.total}
            rowsPerPage={pagination.per_page}
            page={pagination.page}
            backIconButtonProps={{
              'aria-label': '上一頁',
            }}
            nextIconButtonProps={{
              'aria-label': '下一頁',
            }}
            onChangePage={(event, newPage) => {
              handleChange('paginate', { page: newPage })
            }}
            onChangeRowsPerPage={event => {
              handleChange('paginate', { per_page: _.parseInt(event.target.value) })
            }}
          />
        )}
      </Loading>
    </>
  )
}
